<template>
  <table-wrapper>
    <v-data-table
      class="coronet-table--highlightable backup-connectors-table"
      :items="items"
      :loading="loading"
      fixed-header
      :no-data-text="$t('general.noDataAvailable')"
      :items-per-page="-1"
      hide-default-footer
    >
      <template #headers>
        <tr>
          <th class="w-30">{{ $t("cloudBackupAndRestore.connectorsTable.headers.app") }}</th>
          <th class="w-20">{{ $t("cloudBackupAndRestore.connectorsTable.headers.health") }}</th>
          <th class="w-20">
            {{ $t("cloudBackupAndRestore.connectorsTable.headers.backedUpUsers") }}
          </th>
          <th class="w-30">
            {{ $t("cloudBackupAndRestore.connectorsTable.headers.connectionStatus") }}
          </th>
        </tr>
      </template>
      <template #item="{ item, index }">
        <tr :key="index" :data-testid="`backup-connectors-table-row-${index}`">
          <td :data-testid="`backup-connectors-table-app-col-row-${index}`">
            <div class="d-flex align-center ga-2">
              <img
                v-if="workloadIcons[item.name]"
                :src="`/images/app-service-logos/${workloadIcons[item.name]}`"
                :alt="workloadName(item.name)"
                :class="{ 'workload-icon--disconnected': !isWorkloadConnected(item) }"
                class="workload-icon"
              />
              <span class="subtitle2">{{ workloadName(item.name) }}</span>
            </div>
          </td>
          <td :data-testid="`backup-connectors-table-health-col-row-${index}`">
            <span class="body2" :class="getHealthStatus(item).colorClass">
              {{ getHealthStatus(item).text }}
            </span>
          </td>
          <td :data-testid="`backup-connectors-table-usage-col-row-${index}`">
            <span>{{ item.usage }}/</span>
            <span class="text-indigo-medium">{{ item.limit }}</span>
          </td>
          <td :data-testid="`backup-connectors-table-connection-col-row-${index}`">
            <div class="d-flex align-center ga-1">
              <span class="body2" :class="getConnectionStatus(item).colorClass">
                {{ getConnectionStatus(item).text }}
              </span>
              <v-tooltip v-if="getConnectionStatus(item).showInfo" location="top" max-width="320">
                <template #activator="{ props }">
                  <v-icon
                    v-bind="props"
                    size="18"
                    icon="$infoXs"
                    class="connection-info-icon"
                    :class="getConnectionStatus(item).colorClass"
                  />
                </template>
                {{ $t("cloudBackupAndRestore.connectorsTable.connectionTooltip") }}
              </v-tooltip>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </table-wrapper>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import TableWrapper from "@/components/TableWrapper.vue";
import {
  BackupChildHealthStatus,
  KEEPIT_WORKLOAD_ICONS,
  getConnectorHealthStatus,
  isWorkloadConnected,
  type BackupConnector,
  type KeepitWorkload,
} from "@/constants/cloud-backup.ts";

interface StatusMeta {
  text: string;
  colorClass: string;
  showInfo?: boolean;
}

export default defineComponent({
  components: { TableWrapper },
  props: {
    items: {
      type: Array as PropType<BackupConnector[]>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const i18n = useI18n();

    const workloadName = (workload: KeepitWorkload): string =>
      i18n.t(`general.keepitWorkloads.${workload}`);

    const getHealthStatus = (item: BackupConnector): StatusMeta => {
      const healthMetaMap: Record<BackupChildHealthStatus, StatusMeta> = {
        [BackupChildHealthStatus.NONE]: {
          text: i18n.t("cloudBackupAndRestore.connectorsTable.healthStatus.none"),
          colorClass: "text-indigo-medium",
        },
        [BackupChildHealthStatus.HEALTHY]: {
          text: i18n.t("cloudBackupAndRestore.connectorsTable.healthStatus.healthy"),
          colorClass: "text-green-dark",
        },
        [BackupChildHealthStatus.UNHEALTHY]: {
          text: i18n.t("cloudBackupAndRestore.connectorsTable.healthStatus.unhealthy"),
          colorClass: "text-yellow-base",
        },
        [BackupChildHealthStatus.CRITICAL]: {
          text: i18n.t("cloudBackupAndRestore.connectorsTable.healthStatus.critical"),
          colorClass: "text-red-dark",
        },
      };

      return healthMetaMap[getConnectorHealthStatus(item)];
    };

    const getConnectionStatus = (item: BackupConnector): StatusMeta =>
      isWorkloadConnected(item)
        ? {
            text: i18n.t("cloudBackupAndRestore.connectorsTable.connectionStatus.connected"),
            colorClass: "text-green-dark",
          }
        : {
            text: i18n.t("cloudBackupAndRestore.connectorsTable.connectionStatus.notConnected"),
            colorClass: "text-red-dark",
            showInfo: true,
          };

    return {
      workloadIcons: KEEPIT_WORKLOAD_ICONS,
      isWorkloadConnected,
      workloadName,
      getHealthStatus,
      getConnectionStatus,
    };
  },
});
</script>

<style scoped lang="scss">
.v-table {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  box-shadow: none !important;
}

.connection-info-icon:before {
  color: inherit !important;
}

.workload-icon {
  width: 24px;
  height: 24px;

  &--disconnected {
    opacity: 0.5;
  }
}
</style>
